import Axhelper from '@/lib/Axhelper'

/**
 * CustomerService - Handles all customer-related API operations
 * Separates API logic from React hooks and components
 */
class CustomerService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		// console.log(`TCL -> CustomerService.js:10 -> CustomerService -> constructor -> this.baseUrl:`, this.baseUrl)
		this.endpoints = {
			customers: `/api/customers/filters`,
			customersDetails: `/api/customers/details`,
			customersStats: `/api/customers/stats`,
			customersDelete: `/api/customers/deletemany`,
			customersCreate: `/api/customers/`,
			customersUpdate: `/api/customers/`,
			addCommunication: `/api/customers/addcommunication`,
			callWithAnalysis: `/api/customers/callwithanalysis`,
		}

	}

	async getCustomerById(customerId) {
		try {
			if (!customerId) {
				return {
					data: null,
					error: 'Invalid customer ID provided',
				}
			}

			const url = `${this.endpoints.customersDetails}/${encodeURIComponent(customerId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			const customer = response.data ? { ...response.data } : null
			if (customer) {
				customer.communications = Array.isArray(customer.communications) ? customer.communications : []
			}

			return {
				data: customer,
				error: null,
			}
		} catch (error) {
			console.error('CustomerService.getCustomerById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch customer',
			}
		}
	}

	async getCustomersWithFilters(options = {}) {
		try {
			const { limit = 50, sortColumn = 'created', sortDirection = 'desc', filters = {} } = options

			const url = this._buildFilteredUrl({
				limit,
				sortColumn,
				sortDirection,
				filters,
			})

			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			const hasMore = response.data?.length < totalCount

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore,
				headers: response.headers,
			}
		} catch (error) {
			console.error('CustomerService.getCustomersWithFilters error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch customers',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	/**
	 * Get customer statistics
	 * @returns {Promise<{data: Object, error: string|null}>}
	 */
	async getCustomerStats() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.customersStats)

			if (response.error) {
				return {
					data: {},
					error: response.error,
				}
			}

			const raw = response.data || {}

			const totalCustomers =
				raw.totalCustomers ??
				raw.total ??
				raw.count ??
				0

			const currentMonth = raw.currentMonth || {
				count: raw.currentMonthCount ?? 0,
				delta: raw.currentMonthDelta ?? 0,
			}

			const previousMonth = raw.previousMonth || {
				count: raw.previousMonthCount ?? 0,
				delta: raw.previousMonthDelta ?? 0,
			}

			return {
				data: {
					totalCustomers,
					currentMonth,
					previousMonth,
				},
				error: null,
			}
		} catch (error) {
			console.error('CustomerService.getCustomerStats error:', error)
			return {
				data: {},
				error: error.message || 'Failed to fetch customer statistics',
			}
		}
	}

	/**
	 * Delete multiple customers
	 * @param {Array<string>} customerIds - Array of customer IDs to delete
	 * @returns {Promise<{success: boolean, error: string|null, message: string}>}
	 */
	async deleteCustomers(customerIds) {
		try {
			if (!Array.isArray(customerIds) || customerIds.length === 0) {
				return {
					success: false,
					error: 'Invalid customer IDs provided',
					message: null,
				}
			}

			const response = await Axhelper.sendPostAsync(this.endpoints.customersDelete, {
				ids: customerIds,
			})

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Customers deleted successfully',
			}
		} catch (error) {
			console.error('CustomerService.deleteCustomers error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete customers',
				message: null,
			}
		}
	}

	async createCustomer(customerData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.customersCreate, customerData)
			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}
			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('CustomerService.createCustomer error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create customer',
			}
		}
	}

	async updateCustomer(customerId, customerData) {
		try {
			if (!customerId) {
				return {
					success: false,
					error: 'Invalid customer ID provided',
				}
			}

			const url = `${this.endpoints.customersUpdate}/${encodeURIComponent(customerId)}`
			const response = await Axhelper.sendPutAsync(url, customerData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('CustomerService.updateCustomer error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update customer',
			}
		}
	}

	async callWithAnalysis(customerNumber, salesRepNumber) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.callWithAnalysis, { customerNumber, salesRepNumber })
			return { data: response.data || null, error: null };
		} catch (error) {
			console.error('CustomerService.callWithAnalysis error:', error)
			return { success: false, data: null, error: error.message || 'Failed to call with analysis' };
		}
	}

	/**
	 * Build URL for filtered customer requests
	 * @private
	 */
	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		let params = [`limit=${limit}`, `sortcol=${sortColumn}`, `sortdir=${sortDirection}`]

		// Add search text filter
		if (filters.searchText && filters.searchText.length >= 3) {
			params.push(`searchText=${encodeURIComponent(filters.searchText)}`)
		}

		// Add lead therapist filter
		if (filters.therapistId) {
			params.push(`therapistId=${encodeURIComponent(filters.therapistId)}`)
		}

		// Add date filters
		if (filters.fromDate) {
			params.push(`fromDate=${encodeURIComponent(filters.fromDate)}`)
		}

		if (filters.toDate) {
			params.push(`toDate=${encodeURIComponent(filters.toDate)}`)
		}

		return `${this.endpoints.customers}?${params.join('&')}`
	}

	async addCommunication(payload) {
		try {
			if (!payload || !payload.id) {
				return {
					success: false,
					error: 'Invalid customer ID provided',
				}
			}

			const response = await Axhelper.sendPostAsync(this.endpoints.addCommunication, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('CustomerService.addCommunication error:', error)
			return {
				success: false,
				error: error.message || 'Failed to add communication',
			}
		}
	}
}

// Export a singleton instance
export default new CustomerService()
